import { differenceInCalendarDays } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';

import { SUBSCRIPTIONS_HIDDEN } from '@/pages/SettingsPage/components/settings-flags';
import {
  EDIT_SUBSCRIPTION,
  planManageableServices,
} from '@/pages/SubscriptionSettingsPage/subscription-access';
import { planName, renewalOf } from '@/pages/SubscriptionSettingsPage/subscription-display';
import { useAuth } from '@/store/auth/auth.store';
import { useSubscriptionsQuery } from '@/store/subscriptions/subscriptions.store';
import { PaymentStatus } from '@/store/subscriptions/subscriptions.types';

/** The three reminders: from a month out, from a week out, and on the day itself. */
export type TrialStage = 'month' | 'week' | 'lastDay';

export type TrialReminder = {
  stage: TrialStage;
  /** Calendar days until the trial ends; 0 on the last day. */
  daysLeft: number;
  endsAt: number;
  /** "Command", "Navigate" — the plan the trial is of. */
  planLabel: string;
};

const MONTH_DAYS = 30;
const WEEK_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

const stageOf = (daysLeft: number): TrialStage | null => {
  if (daysLeft <= 0) return 'lastDay';
  if (daysLeft <= WEEK_DAYS) return 'week';
  if (daysLeft <= MONTH_DAYS) return 'month';
  return null;
};

const DISMISSED_KEY = 'trial_banner_dismissed';

/**
 * A dismissal holds for one stage of one trial: the month reminder stays closed until the
 * week one is due, the week one comes back the next day. A new trial end — extended by a
 * referral, or another vessel's trial — is a different reminder and shows again.
 */
type Dismissal = { endsAt: number; stage: TrialStage; until: number | null };

const readDismissal = (): Dismissal | null => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? (JSON.parse(raw) as Dismissal) : null;
  } catch {
    return null;
  }
};

const isDismissed = (reminder: TrialReminder, dismissal: Dismissal | null, now: number) =>
  !!dismissal &&
  dismissal.endsAt === reminder.endsAt &&
  dismissal.stage === reminder.stage &&
  (dismissal.until === null || now < dismissal.until);

/**
 * The trial reminder for the current user, or `null` when there is nothing to remind them of.
 *
 * Read across every vessel whose plan they may change — not only the active one — since a
 * trial ends for the vessel whichever account is open. Several vessels on trial make one
 * reminder: the soonest to end. Viewers who can only read a plan are not asked to pick one.
 */
export const useTrialReminder = () => {
  const services = useAuth((state) => state.session?.services);
  const serviceIds = useMemo(
    () =>
      planManageableServices(services)
        .filter((service) => service.rights?.includes(EDIT_SUBSCRIPTION))
        .map((service) => service.service_id),
    [services],
  );
  const { byServiceId } = useSubscriptionsQuery(SUBSCRIPTIONS_HIDDEN ? [] : serviceIds);
  const [dismissal, setDismissal] = useState(readDismissal);
  // Read once per mount: the stage moves on a day boundary, and every page change remounts
  // the header that carries the banner.
  const [now] = useState(() => Date.now());

  const reminder = useMemo<TrialReminder | null>(() => {
    const soonest = [...byServiceId.values()]
      .filter((subscription) => subscription.paymentStatus === PaymentStatus.TRIALING)
      .map((subscription) => ({ subscription, endsAt: renewalOf(subscription).at }))
      .filter((trial): trial is typeof trial & { endsAt: number } => trial.endsAt !== null)
      .filter(({ endsAt }) => endsAt > now)
      .sort((a, b) => a.endsAt - b.endsAt)[0];
    if (!soonest) return null;

    const daysLeft = differenceInCalendarDays(soonest.endsAt, now);
    const stage = stageOf(daysLeft);
    if (!stage) return null;

    return {
      stage,
      daysLeft,
      endsAt: soonest.endsAt,
      planLabel: planName(soonest.subscription),
    };
  }, [byServiceId, now]);

  const dismiss = useCallback(() => {
    if (!reminder || reminder.stage === 'lastDay') return;
    const next: Dismissal = {
      endsAt: reminder.endsAt,
      stage: reminder.stage,
      until: reminder.stage === 'week' ? Date.now() + DAY_MS : null,
    };
    setDismissal(next);
    try {
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      // Private mode or blocked storage: the banner stays closed until the next load.
    }
  }, [reminder]);

  return {
    reminder: reminder && !isDismissed(reminder, dismissal, now) ? reminder : null,
    dismiss,
  };
};
